import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Modal } from "@shared/controls/Modal";

import hero1Webp from "../../assets/images/landing-page/hero1.webp";
import hero1MobileWebp from "../../assets/images/landing-page/hero1-mobile.webp";
import hero2Webp from "../../assets/images/landing-page/hero2.webp";
import hero2MobileWebp from "../../assets/images/landing-page/hero2-mobile.webp";
import hero3Webp from "../../assets/images/landing-page/hero3.webp";
import hero3MobileWebp from "../../assets/images/landing-page/hero3-mobile.webp";
import arrowLeft from "../../assets/images/landing-page/arrow-left.svg";
import arrowRight from "../../assets/images/landing-page/arrow-right.svg";
import playstore from "../../assets/images/landing-page/playstore.svg";
import caret from "../../assets/images/landing-page/caret.svg";
import appstore from "../../assets/images/landing-page/appstore.svg";
import "./styles.css";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.krent.krent";
const APP_STORE_URL = "https://apps.apple.com/ng/app/krent/id6755475197";

const desktopSlides = [hero3Webp, hero2Webp, hero2Webp, hero1Webp];
const mobileSlides = [hero3MobileWebp, hero2MobileWebp, hero2MobileWebp, hero1MobileWebp];

const transitions = [
  { transform: "translateY(-80px)", opacity: 0 },
  { transform: "translateY(80px)", opacity: 0 },
  { transform: "translateX(-80px)", opacity: 0 },
  { transform: "translateX(80px)", opacity: 0 },
  { transform: "scale(0.8)", opacity: 0 },
];

const CampaignHero = () => {
  const [current, setCurrent] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [contentStyle, setContentStyle] = useState<React.CSSProperties>({
    transform: "translateY(0) scale(1)",
    opacity: 1,
  });

  const [isMobile, setIsMobile] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);
  const [showStoreModal, setShowStoreModal] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const slides = isMobile ? mobileSlides : desktopSlides;
  const extendedSlides = [...slides, slides[0]];

  const nextSlide = () => {
    setAnimate(true);
    setCurrent((p) => p + 1);
  };

  useEffect(() => {
    const img = new Image();
    img.src = slides[0];
    img.onload = () => setBgLoaded(true);
  }, [isMobile]);

  const prevSlide = () => {
    if (current === 0) {
      setAnimate(false);
      setCurrent(slides.length);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimate(true);
          setCurrent(slides.length - 1);
        });
      });
    } else {
      setAnimate(true);
      setCurrent((p) => p - 1);
    }
  };

  useEffect(() => {
    const i = setInterval(nextSlide, 5000);
    return () => clearInterval(i);
  }, [current]);

  useEffect(() => {
    if (current === slides.length) {
      const t = setTimeout(() => {
        setAnimate(false);
        setCurrent(0);
      }, 800);
      return () => clearTimeout(t);
    }
  }, [current]);

  const activeIndex = current === slides.length ? 0 : current;

  useEffect(() => {
    const random = transitions[Math.floor(Math.random() * transitions.length)];
    setContentStyle({ ...random, transition: "none" });
    const t = setTimeout(() => {
      setContentStyle({
        transform: "translateY(0) scale(1)",
        opacity: 1,
        transition:
          "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      });
    }, 20);
    return () => clearTimeout(t);
  }, [activeIndex]);

  const renderSlideHeading = () => {
    switch (activeIndex) {
      case 0:
        return (
          <h1 className="text-white fw-600 campaign-title">
            Find your perfect <span className="text-primary">shortlet</span> and
            book in minutes
          </h1>
        );
      case 1:
        return (
          <div className="campaign-rent-heading">
            <h1 className="text-white fw-600 campaign-title">Rent Smart in <span className="text-primary">2026</span>.</h1>
            <h2 className="text-white campaign-subtitle"><span className="text-primary">Verified</span> Homes Only.</h2>
            <p className="text-white campaign-rent-tagline">Zero scams, Zero Stress.</p>
          </div>
        );
      case 2:
        return (
          <h1 className="text-white fw-600 campaign-title">
            Turn Your <span className="text-primary">Property</span> into a{" "}
            <span className="text-primary">Money</span>-Making{" "}
            <span className="text-primary">Asset</span>
          </h1>
        );
      case 3:
        return (
          <div className="campaign-app-heading">
            <h1 className="text-white fw-600 campaign-title mb-2">
              Download the Krent App
            </h1>
            <p className="text-white campaign-subtitle mb-0">
              Available on{" "}
              <span className="text-primary fw-semibold">Google Play</span>
              {" & "}
              <span className="text-primary fw-semibold">App Store</span>
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const renderSlideCta = () => {
    switch (activeIndex) {
      case 0:
        return (
          <Link to="/shortlet" className="btn btn-primary text-white">
            Find a Shortlet <img src={caret} alt="" />
          </Link>
        );
      case 1:
        return (
          <Link to="/property-for-rent" className="btn btn-primary text-white">
            Learn more <img src={caret} alt="" />
          </Link>
        );
      case 2:
        return (
          <a href="/krentmedia" className="btn btn-primary text-white">
            Learn More <img src={caret} alt="" />
          </a>
        );
      case 3:
        return (
          <button
            type="button"
            className="btn btn-primary text-white"
            onClick={() => setShowStoreModal(true)}
          >
            Get Started <img src={caret} alt="" />
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <section
      className={`campaign-hero${
        isMobile && activeIndex < 3 ? " campaign-hero--wide-text" : ""
      }`}
    >
      <div
        className="slider-track"
        style={{
          width: `${extendedSlides.length * 100}%`,
          transform: `translateX(-${current * 100}vw)`,
          transition: animate ? "transform 0.8s ease-in-out" : "none",
        }}
      >
        {extendedSlides.map((img, i) => (
          <div key={i} className="campaign-bg">
            <div className="bg-placeholder" />
            {bgLoaded && (
              <div
                className="bg-image loaded"
                style={{ backgroundImage: `url(${img})` }}
              />
            )}
          </div>
        ))}
      </div>

      <button onClick={prevSlide} className="nav-btn left">
        <img src={arrowLeft} alt="Previous" />
      </button>

      <button onClick={nextSlide} className="nav-btn right">
        <img src={arrowRight} alt="Next" />
      </button>
      <div className="container-fluid position-relative h-100 d-flex align-items-center">
        <div className="container hero-container flex-column flex-lg-row align-items-center justify-content-between gap-5">
          <div>
            <div style={contentStyle}>
              {renderSlideHeading()}

              <div className="btn-more">{renderSlideCta()}</div>
              {activeIndex === 1 && <p className="campaign-rent-footer">Verified properties | Real Agents | 3D Tours</p>}
            </div>

            {!isMobile && activeIndex === 3 && (
              <div className="campaign-apps">
                <div className="apps-wrapper">
                  <p className="text-black fw-semibold text-center download-text">
                    Download our mobile app on:
                  </p>

                  <div className="store-buttons">
                    <a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="btn btn-dark store-btn"
                    >
                      <img src={playstore} alt="Google Play" />
                      Play Store
                    </a>

                    <a
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="btn text-dark store-btn"
                      style={{ backgroundColor: "#F4F4F4" }}
                    >
                      <img src={appstore} alt="Apple Store" />
                      Apple Store
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white col-12 col-lg-7 rounded d-none d-lg-block" />
        </div>
      </div>

      <div className="indicators">
        {slides.map((_, i) => (
          <div
            key={i}
            onClick={() => setCurrent(i)}
            className={`indicator ${activeIndex === i ? "active" : ""}`}
          />
        ))}
      </div>

      {createPortal(
        <Modal
          show={showStoreModal}
          onClose={() => setShowStoreModal(false)}
          size={isMobile ? "md" : "sm"}
          position="center"
        >
          <div className="p-3 p-md-4 text-center store-picker-modal">
            <h5 className="fw-bold mb-2 font-onest">Download the Krent App</h5>
            <p className="text-muted mb-4 fs-14">
              Choose your app store to get started
            </p>
            <div className="d-flex flex-column gap-3">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="btn btn-dark store-picker-btn"
                onClick={() => setShowStoreModal(false)}
              >
                <img src={playstore} alt="" />
                Google Play
              </a>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="btn store-picker-btn"
                style={{ backgroundColor: "#F4F4F4", color: "#212529" }}
                onClick={() => setShowStoreModal(false)}
              >
                <img src={appstore} alt="" />
                App Store
              </a>
            </div>
          </div>
        </Modal>,
        document.body,
      )}
    </section>
  );
};

export default CampaignHero;
